import {Form, Formik} from 'formik'
import {
  bulkUploadVendors,
  downloadVendorTemplate,
  getOrgProfiles, getStates,
  markDeleted,
  updatePaymentStatus,
} from '../../services/order.service'
import MoneyField from '../money-field/money-field'
import clsx from 'clsx'
import InputField from '../input-field/input-field'
import {FC, useEffect, useRef, useState} from 'react'
import {useAuth} from '../../contexts/auth.context'
import readXlsxFile from 'read-excel-file'
import {Button} from 'react-bootstrap'
import parse from 'html-react-parser'

type Props = {
  states: any[],
  orgProfiles: any[]
}

const BulkVendorModal: FC<Props> = ({states, orgProfiles}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const {auth} = useAuth();
  const [showSplashScreen, setShowSplashScreen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitData, setSubmitData] = useState([]);
  const [parseError, setParseError] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [filename, setFilename] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  const schema = {
    "State*": {
      prop: 'state',
      type: String,
      required: true
    },
    "County": {
      prop: 'county',
      type: String
    },
    "Search Company*": {
      prop: 'searchCompany',
      type: String,
      required: true
    }
  }

  const downloadTemplate = () => {
    setDownloading(true);
    downloadVendorTemplate().then((data: any) => {
      console.log(data);
      if(data?.message) {
        var a = document.createElement("a"); //Create <a>
        a.href = "data:application/octet-stream;base64," + data.message; //Image Base64 Goes here
        a.download = "Vendor Template.xlsx"; //File name Here
        a.click(); //Downloaded file
      }
    }).finally(() => setDownloading(false));
  }

  const reset = () => {
    if(fileRef.current) {
      fileRef.current.value = "";
      setFilename("");
      setParseError("");
      setError('');
      setSuccess('');
      setSubmitData([]);
    }
  }

  const submit = () => {
    setError('');
    setSuccess('');
    setLoading(true);
    if(auth?.api_token) {
      bulkUploadVendors(submitData).then((data: any) => {
        if(data?.message) {
          setSuccess(data.message);
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }
        else if(data?.error) {
          setError(data.error);
        }
      }).catch((err) => {
        setError(err);
      }).finally(() => setLoading(false));
    }
  }

  const handleFiles = async (e: any) => {
    const file = e.target.files[0];
    if(file) {
      setError('');
      setSuccess('');
      setFilename(file.name);
      const bufferArray = await file.arrayBuffer();
      const {rows, errors}: any = await readXlsxFile(bufferArray, {
        schema,
        schemaPropertyValueForMissingColumn: null,
        schemaPropertyValueForEmptyCell: null,
        getEmptyObjectValue: () => ""
      });
      if(rows && rows.length != 0) {
        setParseError('');
        var tempError = "";
        errors.forEach((item: any) => {
          tempError = tempError + `Error: Missing field ${item.column} in row ${item.row}<br/>`
          setParseError(tempError);
        });
        if(!tempError) {
          rows.forEach((item: any, index: any) => {
            var stateValid = orgProfiles.findIndex((o: any) => item["state"] === o.stateCode || item["state"] === String(o.stateId) || item["state"] === o.stateName);
            if(stateValid != -1) {
              item["stateId"] = orgProfiles[stateValid].stateId;
            }
            else {
              tempError = tempError + `Error: Invalid state '${item["state"]}' at row ${index + 2}<br/>`
              setParseError(tempError);
            }
            if(item["county"] && item["county"].length != 0) {
              var validCounties = states.filter((s: any) => String(item["stateId"]) === String(s.stateId))[0]?.counties;
              if(validCounties?.length != 0) {
                var countyValid = validCounties.findIndex((c: any) => item["county"] === c.countyName || item["county"] === String(c.countyId) || item["county"] === c.countyCode);
                if(countyValid != -1) {
                  item["countyId"] = validCounties[countyValid].countyId;
                }
                else {
                  tempError = tempError + `Error: '${item["county"]}' is not a valid county for state '${item["state"]}' at row ${index + 2}<br/>`
                  setParseError(tempError);
                }
              }
            }
            var validSearchers = orgProfiles[stateValid]?.searchCompanies;
            if(validSearchers?.length != 0) {
              var searcherValid = validSearchers.findIndex((s: any) => item["searchCompany"] === s.searcher_SCOMP_Code || item["searchCompany"] === String(s.searcher_SCOMP_ID) || item["searchCompany"] === s.searcher_SCOMP_Company);
              if(searcherValid != -1) {
                item["searchCompany"] = validSearchers[searcherValid].searcher_SCOMP_ID;
              }
              else {
                tempError = tempError + `Error: Invalid search company '${item["searchCompany"]}' at row ${index + 2}<br/>`
                setParseError(tempError);
              }
            }
            else {
              tempError = tempError + `Error: No valid searcher data found for state '${item["state"]}' at row ${index + 2}<br/>`
              setParseError(tempError);
            }
          });
        }
        if(!tempError) {
          setSubmitData(rows);
        }
      }
      else {
        setParseError('No rows found in file');
      }
    }
  }

  return (
    <div className='modal fade' id='bulk-vendor-upload' aria-hidden='true' ref={modalRef}>
      <div className='modal-dialog modal-dialog-centered mw-900px'>
        <div className='modal-content bg-white'>
          <div className='modal-header pt-0'>
            <h4 className='fw-semibold text-primary'>Bulk Vendor Assignment</h4>
            <button type="button" className="close border-0 bg-white" aria-label="Close" data-bs-dismiss="modal">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div className='modal-body scroll-y'>
            <div className='card-body py-6'>
                {success &&
                  <div className='px-10 py-5 alert alert-success'>
                    <div className='alert-text font-weight-bold text-primary'>{success}</div>
                  </div>
                }
                {error &&
                  <div className='px-10 py-5 alert alert-danger'>
                    <div className='alert-text font-weight-bold'>{error}</div>
                  </div>
                }
                <Button
                  variant='primary'
                  className={'btn btn-lg btn-primary b-solid position-relative w-300px mw-300px mb-5 mx-2'}
                  onClick={downloadTemplate}
                >
                  {downloading && (
                    <span className='indicator-progress' style={{display: 'block'}}>
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
                  )}
                  {!downloading && <span className='indicator-label'>Download template</span>}
                </Button>
                <h5 className={'my-5'}>Upload file</h5>
                <div className="d-flex flex-column">
                  <button className={`btn btn-lg btn-cancel b-solid position-relative mb-5 mx-2 mw-300px cursor-pointer`}>
                    Select File
                    <input type="file" data-testid="fileUploader" name="fileUploader" ref={fileRef} className='position-absolute w-100' style={{opacity:'0'}} accept='.csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel'  onChange={(e)=>handleFiles(e)} />
                  </button>
                  <span>{filename}</span>
                </div>
                {parseError && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{parse(parseError)}</span>
                    </div>
                  </div>
                )}
              </div>
            <div className='text-center d-flex flex-dir justify-content-center modal-buttons'>
              <button className={`btn btn-lg btn-cancel b-solid position-relative mb-5 mx-2 cursor-pointer`} onClick={reset}>
                <span className="indicator-label">Reset</span>
              </button>
              <button disabled={parseError !== "" || submitData.length === 0} className={`btn btn-lg btn-primary b-solid position-relative mb-5 mx-2 cursor-pointer`} onClick={() => submit()}>
                {!loading  && <span className='indicator-label'>Submit</span>}
                {loading && (
                  <span className='indicator-progress' style={{display: 'block'}}>
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BulkVendorModal;